import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
} from "react";
import axios from "../utils/axiosInstance";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // decoded token then hydrated
  const [loading, setLoading] = useState(true);

  const decodeToken = (token) => {
    if (!token || typeof token !== "string" || !token.includes(".")) return null;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        "="
      );
      return JSON.parse(atob(padded));
    } catch (e) {
      console.error("Token decoding failed", e);
      return null;
    }
  };

  const isTokenExpired = (token) => {
    const decoded = decodeToken(token);
    if (!decoded?.exp) return true;
    return decoded.exp * 1000 < Date.now();
  };

  const storeToken = useCallback((token) => {
    localStorage.setItem("authToken", token);
  }, []);

  const clearToken = useCallback(() => {
    localStorage.removeItem("authToken");
  }, []);

  // Hydrate from /profile
  const fetchProfileMe = useCallback(async () => {
    try {
      const res = await axios.get("/profile");

      // Your backend might return either {user, profile} OR a flat object.
      const apiUser = res.data?.user || res.data || null;
      const apiProfile = res.data?.profile || null;

      if (apiUser && typeof apiUser === "object") {
        setUser((prev) => ({
          ...(prev || {}),
          ...apiUser,
          profile: {
            ...(prev?.profile || {}),
            ...(apiUser.profile || apiUser.UserProfile || {}),
            ...(apiProfile || {}),
          },
        }));
      }

      return res.data;
    } catch (err) {
      console.error(
        "fetchProfileMe failed:",
        err?.response?.status,
        err?.response?.data || err.message
      );
      return null;
    }
  }, []);

  // ✅ login = store token + set decoded + hydrate. NO NAVIGATION HERE.
  const login = useCallback(
    async (token) => {
      const decoded = decodeToken(token);
      if (!decoded) throw new Error("Invalid token");

      storeToken(token);
      setUser(decoded);

      await fetchProfileMe();

      // return decoded so UI can route
      return decoded;
    },
    [storeToken, fetchProfileMe]
  );

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    // NOTE: let your UI redirect; do not hard redirect here
  }, [clearToken]);

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        if (mounted) setLoading(false);
        return;
      }

      if (isTokenExpired(token)) {
        clearToken();
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      const decoded = decodeToken(token);
      if (decoded && mounted) setUser(decoded);

      await fetchProfileMe();

      if (mounted) setLoading(false);
    };

    boot();

    return () => {
      mounted = false;
    };
  }, [fetchProfileMe, clearToken]);

  const contextValue = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      refreshUser: fetchProfileMe,
    }),
    [user, loading, login, logout, fetchProfileMe]
  );

  if (loading) return <div className="text-center mt-10">Loading...</div>;

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);